import React, { forwardRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
// import { Input } from '../../ui/input';
import { Calendar } from 'lucide-react';
import { Input } from './input';
import { cn } from './utils';

interface CustomDatePickerProps {
  value?: string;
  onChange?: (date: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  error?: boolean;
  showTimeSelect?: boolean;
  timeFormat?: string;
  dateFormat?: string;
}

const parseDateOnlyValue = (value?: string) => {
  if (!value) {
    return null;
  }

  const trimmed = value.trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (match) {
    const [, yearText, monthText, dayText] = match;
    return new Date(Number(yearText), Number(monthText) - 1, Number(dayText), 0, 0, 0, 0);
  }

  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateOnlyValue = (date: Date) => {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const parseTimeOnlyValue = (value?: string) => {
  if (!value) {
    return null;
  }

  const trimmed = value.trim();
  const match = /^(\d{2}):(\d{2})$/.exec(trimmed);
  if (!match) {
    return null;
  }

  const [, hourText, minuteText] = match;
  const now = new Date();
  return new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    Number(hourText),
    Number(minuteText),
    0,
    0,
  );
};

const formatTimeOnlyValue = (date: Date) => {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

// Custom input component for DatePicker
const DatePickerInput = forwardRef<HTMLInputElement, any>(({ value, onClick, placeholder, disabled, className, error }, ref) => (
  <div className="relative w-full">
    <Input
      ref={ref}
      value={value}
      onClick={onClick}
      placeholder={placeholder}
      disabled={disabled}
      readOnly
      className={`${className} ${error ? 'border-red-500' : ''} cursor-pointer pr-10`}
    />
    <Calendar
      className={cn(
        "pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 transform",
        disabled && value ? "text-sky-700" : "text-gray-400",
      )}
    />
  </div>
));

DatePickerInput.displayName = 'DatePickerInput';

export const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  value,
  onChange,
  placeholder = "DD/MM/YYYY",
  disabled = false,
  className = "",
  error = false,
  showTimeSelect = false,
  timeFormat = "HH:mm",
  dateFormat = "dd/MM/yyyy"
}) => {
  const selectedDate = parseDateOnlyValue(value);

  const handleDateChange = (date: Date | null) => {
    if (date && onChange) {
      onChange(formatDateOnlyValue(date));
    } else if (!date && onChange) {
      onChange('');
    }
  };

  return (
    <DatePicker
      selected={selectedDate}
      onChange={handleDateChange}
      dateFormat={showTimeSelect ? `${dateFormat} ${timeFormat}` : dateFormat}
      showTimeSelect={showTimeSelect}
      timeFormat={timeFormat}
      timeIntervals={15}
      customInput={
        <DatePickerInput
          placeholder={placeholder}
          disabled={disabled}
          className={className}
          error={error}
        />
      }
      disabled={disabled}
      placeholderText={placeholder}
      wrapperClassName="w-full"
      // Make sure Monday is first day of week for international users
      calendarStartDay={1}
      // Show year dropdown
      showYearDropdown
      yearDropdownItemNumber={10}
      scrollableYearDropdown
    />
  );
};

export const CustomDateTimePicker: React.FC<CustomDatePickerProps> = ({
  value,
  onChange,
  placeholder = "DD/MM/YYYY HH:mm",
  disabled = false,
  className = "",
  error = false,
}) => {
  // Convert string value to Date object for datetime
  const selectedDate = value ? new Date(value) : null;

  // Handle datetime change
  const handleDateTimeChange = (date: Date | null) => {
    if (date && onChange) {
      // For datetime, we want to preserve both date and time
      onChange(date.toISOString());
    } else if (!date && onChange) {
      onChange('');
    }
  };

  return (
    <DatePicker
      selected={selectedDate}
      onChange={handleDateTimeChange}
      dateFormat="dd/MM/yyyy HH:mm"
      showTimeSelect={true}
      timeFormat="HH:mm"
      timeIntervals={15}
      customInput={
        <DatePickerInput
          placeholder={placeholder}
          disabled={disabled}
          className={className}
          error={error}
        />
      }
      disabled={disabled}
      placeholderText={placeholder}
      wrapperClassName="w-full"
      calendarStartDay={1}
      showYearDropdown
      yearDropdownItemNumber={10}
      scrollableYearDropdown
    />
  );
};

export const CustomTimePicker: React.FC<CustomDatePickerProps> = ({
  value,
  onChange,
  placeholder = "HH:mm",
  disabled = false,
  className = "",
  error = false,
}) => {
  const selectedTime = parseTimeOnlyValue(value);

  const handleTimeChange = (date: Date | null) => {
    if (date && onChange) {
      onChange(formatTimeOnlyValue(date));
    } else if (!date && onChange) {
      onChange('');
    }
  };

  return (
    <DatePicker
      selected={selectedTime}
      onChange={handleTimeChange}
      dateFormat="HH:mm"
      showTimeSelect
      showTimeSelectOnly
      timeFormat="HH:mm"
      timeIntervals={5}
      customInput={
        <DatePickerInput
          placeholder={placeholder}
          disabled={disabled}
          className={className}
          error={error}
        />
      }
      disabled={disabled}
      placeholderText={placeholder}
      wrapperClassName="w-full"
      timeCaption="Time"
    />
  );
};
